import { ForbiddenError } from '@domain/errors';
import type { AppUser, AuthenticatedAdmin } from '@domain/entities';
import type { AccessPolicy, FileExporter } from '@application/ports';

const ROLE_FR: Record<string, string> = {
  maman: 'Maman', papa: 'Papa', 'grand-mere': 'Grand-mère', 'grand-pere': 'Grand-père',
  tante: 'Tante', oncle: 'Oncle', 'futur-parent': 'Futur parent', autre: 'Autre',
};

/** Génère et déclenche le téléchargement d'un CSV via un Blob. */
export class BrowserFileExporter implements FileExporter {
  exportCsv(filename: string, rows: readonly AppUser[]): void {
    const cols = [
      'Nom', 'Rôle', 'Email', 'Téléphone', 'Pays', 'Ville',
      'Grossesse', 'Date accouchement', 'Sexe bébé', '1er enfant', 'Âge',
      'Langue', 'Conseils hebdo', 'Actualités locales', 'Statut', 'Inscrite le',
    ];
    const escape = (value: string | number | boolean | null | undefined): string =>
      `"${String(value ?? '').replace(/"/g, '""')}"`;
    const body = rows
      .map((u) =>
        [
          u.name,
          ROLE_FR[u.role] ?? u.role,
          u.email,
          u.phone,
          u.countryCode,
          u.city,
          u.pregnancy,
          u.dueDate,
          u.babySex === 'garcon' ? 'Garçon' : u.babySex === 'fille' ? 'Fille' : '',
          u.isFirstChild === true ? 'Oui' : u.isFirstChild === false ? 'Non' : '',
          u.age,
          u.locale === 'en' ? 'English' : 'Français',
          u.wantsWeeklyTips ? 'Oui' : 'Non',
          u.wantsLocalNews ? 'Oui' : 'Non',
          u.status === 'active' ? 'Actif' : 'Désactivé',
          new Date(u.registeredAt).toLocaleDateString('fr-FR'),
        ]
          .map(escape)
          .join(','),
      )
      .join('\n');

    const blob = new Blob([`﻿${cols.join(',')}\n${body}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }
}

/**
 * Politique RBAC côté client. Purement défensive : la source de vérité
 * reste le backend, qui doit renvoyer 403 sur les mêmes règles.
 */
export class SessionAccessPolicy implements AccessPolicy {
  private current: AuthenticatedAdmin | null = null;

  setCurrentAdmin(admin: AuthenticatedAdmin | null): void {
    this.current = admin;
  }

  isSuperAdmin(): boolean {
    return this.current?.role === 'SUPER_ADMIN';
  }

  assertSuperAdmin(action = 'effectuer cette action'): void {
    if (!this.isSuperAdmin()) {
      throw new ForbiddenError(`Réservé au super-admin : impossible de ${action}.`);
    }
  }
}
